/**
 * IntersectionObserver —— https://developer.mozilla.org/zh-CN/docs/Web/API/Intersection_Observer_API
 * @description 视频懒加载
 * - <video data-src={src} data-poster={poster} autoPlay playsInline loop muted/>
 * @param {number} rootMarginY 上下方向 视口窗的扩张/收缩距离
 * @param {number} rootMarginX 左右方向 视口窗的扩张/收缩距离
 * @param {number|string} threshold 视频的可见程度提醒断点
 * @param {Node} dom 在这个dom节点下搜索video[data-src] （组件内可用）
 */

function VideoLazyLoad(rootMarginY = 150, rootMarginX = 0, threshold = [0, 0.25], dom = document) {
  function videoLoaded(video) {
    return video.getAttribute('src');
  }

  const lazyVideos = [].slice.call((dom || document)?.querySelectorAll('video[data-src]'));

  if ('IntersectionObserver' in window) {
    let lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((_video) => {
          const video = _video.target;
          const videoPlaying =
            video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_CURRENT_DATA;
          if (!_video.isIntersecting && videoLoaded(video) && videoPlaying) {
            video.pause();
          }
          /**
           * @description 检查视频是否加载完毕
           *   —— 通过监听 video 的 timeupdate 事件，当视频的缓存时长大于或等于视频时长时，说明视频已经加载完毕
           *   —— 如何加载完毕则在视频上添加一个自定义属性 data-loaded，用于后续判断视频是否加载完毕，避免重复加载
           */
          function checkVideoLoadStatus() {
            if (!video?.buffered?.length || video?.buffered.length === 0) return;
            const bufferedSeconds = video.buffered?.end?.(0); // 已缓冲的视频时长（以秒为单位）
            const totalSeconds = video.duration; // 视频的总时长（以秒为单位）
            if (bufferedSeconds >= totalSeconds - 0.5) {
              // 这里额外减去 0.5 秒，避免视频缓冲未完成就被判断为已加载完毕
              video.dataset.loaded = true;
              video.removeEventListener('timeupdate', checkVideoLoadStatus);
            }
          }
          video.addEventListener('timeupdate', checkVideoLoadStatus);

          // 如果视频缓冲未完成，需要清空 src，避免视频加载后续资源
          if (!_video.isIntersecting && video.src && !video.dataset?.loaded) {
            video.removeAttribute('src');
            video.load();
          }

          // 可见度0-2 先配置视频封面，视频不允许播放。
          if (_video.intersectionRatio > 0 && _video.intersectionRatio < 0.25) {
            if (!video.getAttribute('poster')) {
              if (video.getAttribute('data-poster')) {
                video.poster = video.getAttribute('data-poster');
              } else {
                video.src = video.getAttribute('data-src');
              }
            }

            if (videoLoaded(video) && videoPlaying) {
              video.pause();
            }
          }

          if (_video.intersectionRatio >= 0.25) {
            if (videoLoaded(video)) {
              const playPromise = video?.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  // console.log('video slider play error', err);
                });
              }
            } else {
              if (!video.getAttribute('poster')) {
                video.poster = video.getAttribute('data-poster');
              }
              video.src = video.getAttribute('data-src');
              video.load();
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  // console.log('lazyload video play error', err);
                });
              }
            }
          }
        });
      },
      {
        root: document.getElementById('_next'),
        rootMargin: `${typeof rootMarginY === 'string' ? rootMarginY : rootMarginY + 'px'} ${
          typeof rootMarginX === 'string' ? rootMarginX : rootMarginX + 'px'
        }`,
        threshold: threshold ?? [0, 0.25],
      }
    );

    lazyVideos.forEach((lazyVideo) => {
      lazyVideoObserver.observe(lazyVideo);
    });
  } else {
    lazyVideos.forEach((video) => {
      video.src = video.getAttribute('data-src');
      video.poster = video.getAttribute('data-poster');
      video.load();
    });
  }
}

/** 视频懒加载，但是在首次进入视野时加载一次，之后是否暂停、播放由用户手动控制。 */
export function VideoLazyLoadFirst(threshold = 300) {
  const lazyVideos = [].slice.call(document.querySelectorAll('video[data-src]'));

  if ('IntersectionObserver' in window) {
    let lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((_video) => {
          const video = _video.target;
          if (_video.isIntersecting && !video.src) {
            video.poster = video.getAttribute('data-poster');
            video.src = video.getAttribute('data-src');
            video.load();
            const playPromise = video?.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // console.log('video lazy play error', err);
              });
            }
          }
        });
      },
      {
        root: document.getElementById('_next'),
        rootMargin: `${threshold}px 0px`,
      }
    );

    lazyVideos.forEach((lazyVideo) => {
      lazyVideoObserver.observe(lazyVideo);
    });
  } else {
    lazyVideos.forEach((video) => {
      video.src = video.getAttribute('data-src');
      video.poster = video.getAttribute('data-poster');
      video.load();
    });
  }
}

/** 视频懒加载，但不控制视频播放暂停 */
export function VideoLazyLoadOnly(threshold = 50) {
  const lazyVideos = [].slice.call(document.querySelectorAll('video[data-src]'));

  if ('IntersectionObserver' in window) {
    let lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((_video) => {
          const video = _video.target;

          function checkVideoLoadStatus() {
            if (!video?.buffered?.length || video?.buffered.length === 0) return;
            const bufferedSeconds = video.buffered?.end?.(0); // 已缓冲的视频时长（以秒为单位）
            const totalSeconds = video.duration; // 视频的总时长（以秒为单位）
            if (bufferedSeconds >= totalSeconds - 0.5) {
              // 这里额外减去 0.5 秒，避免视频缓冲未完成就被判断为已加载完毕
              video.dataset.loaded = true;
              video.removeEventListener('timeupdate', checkVideoLoadStatus);
            }
          }
          video.addEventListener('timeupdate', checkVideoLoadStatus);

          // 如果视频缓冲未完成，需要清空 src，避免视频加载后续资源
          if (!_video.isIntersecting && video.src && !video.dataset?.loaded) {
            video.removeAttribute('src');
            video.load();
          }

          // 视频进入视口，开始加载
          if (_video.isIntersecting && !video.src) {
            video.poster = video.getAttribute('data-poster');
            video.src = video.getAttribute('data-src');
            video.load();
          }
        });
      },
      {
        root: document.getElementById('_next'),
        rootMargin: `${threshold}px 0px`,
      }
    );

    lazyVideos.forEach((lazyVideo) => {
      lazyVideoObserver.observe(lazyVideo);
    });
  } else {
    lazyVideos.forEach((video) => {
      video.src = video.getAttribute('data-src');
      video.poster = video.getAttribute('data-poster');
      video.load();
    });
  }
}

/**
 *
 * @param {HTMLVideoElement} videoEle 视频元素
 * @param {string} eleTag 自定义属性标识，默认data-src|poster，添加标识后为data-xxx-src|poster
 * @param {{rootMarginY?: number | string, rootMarginX?: number | string, threshold?: []}} options IntersectionObserver的配置属性
 * @returns {IntersectionObserver | null} observer
 */
export function VideoLazyLoadSingle(videoEle, eleTag = '', { rootMarginY = 150, rootMarginX = 0, threshold }) {
  const videoLoaded = (video) => video.getAttribute('src');
  const videoPoster = eleTag ? `data-${eleTag}-poster` : 'data-poster';
  const videoSrc = eleTag ? `data-${eleTag}-src` : 'data-src';
  let lazyVideoObserver = null;

  if ('IntersectionObserver' in window) {
    lazyVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((_video) => {
          const video = _video.target;
          const videoPlaying =
            video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_CURRENT_DATA;
          if (!_video.isIntersecting && videoLoaded(video) && videoPlaying) {
            video.pause();
          }
          /**
           * @description 检查视频是否加载完毕
           *   —— 通过监听 video 的 timeupdate 事件，当视频的缓存时长大于或等于视频时长时，说明视频已经加载完毕
           *   —— 如何加载完毕则在视频上添加一个自定义属性 data-loaded，用于后续判断视频是否加载完毕，避免重复加载
           */
          function checkVideoLoadStatus() {
            if (!video?.buffered?.length || video?.buffered?.length === 0) return;
            const bufferedSeconds = video.buffered?.end?.(0); // 已缓冲的视频时长（以秒为单位）
            const totalSeconds = video.duration; // 视频的总时长（以秒为单位）
            if (bufferedSeconds >= totalSeconds - 0.5) {
              // 这里额外减去 0.5 秒，避免视频缓冲未完成就被判断为已加载完毕
              video.dataset.loaded = true;
              video.removeEventListener('timeupdate', checkVideoLoadStatus);
            }
          }
          video.addEventListener('timeupdate', checkVideoLoadStatus);

          // 如果视频缓冲未完成，需要清空 src，避免视频加载后续资源
          if (!_video.isIntersecting && video.src && !video.dataset?.loaded) {
            video.removeAttribute('src');
            video.load();
          }

          // 可见度0-2 先配置视频封面，视频不允许播放。
          if (_video.intersectionRatio < 0.25) {
            if (videoLoaded(video) && videoPlaying) video.pause();

            if (_video.intersectionRatio > 0) {
              if (!video.getAttribute('poster') && video.getAttribute(videoPoster)) {
                video.poster = video.getAttribute(videoPoster);
              }
            }
          }

          if (_video.intersectionRatio >= 0.25) {
            if (!video.getAttribute('poster')) {
              video.poster = video.getAttribute(videoPoster);
            }
            if (!videoLoaded(video)) {
              video.src = video.getAttribute(videoSrc);
              video.load();
            }
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // console.log('video slider play error', err);
              });
            }
          }
        });
      },
      {
        root: document.getElementById('_next'),
        rootMargin: `${typeof rootMarginY === 'string' ? rootMarginY : rootMarginY + 'px'} ${
          typeof rootMarginX === 'string' ? rootMarginX : rootMarginX + 'px'
        }`,
        threshold: threshold ?? [0, 0.25],
      }
    );
    lazyVideoObserver.observe(videoEle);
  } else {
    videoEle.src = videoEle.getAttribute(videoSrc);
    videoEle.poster = videoEle.getAttribute(videoPoster);
    videoEle.load();
  }
  return lazyVideoObserver;
}

export default VideoLazyLoad;
